import { useEffect, useMemo, useState } from "react";

import { Product } from "@transfr-inc/dashboard-components/constants";

import { useApiRequest } from "@lib/hooks";
import { useContainer } from "@src/contexts/container-context";
import { useOrganizationDetails, useProducts } from "@queries";
import { ProfessionalLearningProduct } from "@components/custom/dropdowns/product-dropdown-list";

export const BUILD_YOUR_OWN_PROGRAM = { id: "build-your-own", title: "Build your own" };

/**
 * Resolves the product ID list sent to the API when creating a classroom.
 */
export function getProductIdsForClassroomCreation(product) {
  const productId = product.id;
  if (productId === Product.TRK || productId === Product.CE_TRK) {
    return [Product.TRK, Product.CE];
  }
  if (productId === "TRK_TS") {
    return [Product.TS, Product.TRK, Product.CE];
  }
  if (productId === ProfessionalLearningProduct.id) {
    return [Product.PROG];
  }
  return [productId];
}

export const useClassroomCreationData = (
  orgCode,
  orgSelectionEnabled = true
) => {
  const products = useProducts();
  const { courseService } = useContainer();
  const { data: organizationDetails } = useOrganizationDetails(orgCode);

  const [preCalculatedData, setPreCalculatedData] = useState({
    availableProducts: products,
    showProgramProduct: false,
    programs: [],
    isLoading: false,
  });

  const {
    loading: loadingOrganizationPrograms,
    response: organizationPrograms,
    sendRequest: getOrganizationPrograms,
  } = useApiRequest(
    () => courseService.getOrganizationPrograms(orgCode),
    false
  );

  const calculateAvailableProducts = useMemo(() => {
    const hasProfessionalLearning =
      !!orgCode && !!organizationDetails?.professionalLearning;

    if (!orgCode || orgSelectionEnabled) {
      const availableProducts = hasProfessionalLearning
        ? [...products, ProfessionalLearningProduct]
        : products;
      return {
        availableProducts,
        showProgramProduct: products.includes(Product.PROG),
      };
    }

    const hasAssignedPrograms =
      organizationPrograms && organizationPrograms.length > 0;
    const includesProg = products.includes(Product.PROG);

    let filteredProducts;
    if (hasAssignedPrograms && includesProg) {
      filteredProducts = products;
    } else {
      filteredProducts = products.filter((product) => product !== Product.PROG);
    }

    if (hasProfessionalLearning) {
      filteredProducts = [...filteredProducts, ProfessionalLearningProduct];
    }

    return {
      availableProducts: filteredProducts,
      showProgramProduct: hasAssignedPrograms && includesProg,
    };
  }, [
    orgCode,
    orgSelectionEnabled,
    products,
    organizationPrograms,
    organizationDetails,
  ]);

  useEffect(() => {
    if (!orgCode) return;

    setPreCalculatedData((prev) => ({ ...prev, isLoading: true }));
    getOrganizationPrograms().finally(() => {
      setPreCalculatedData((prev) => ({ ...prev, isLoading: false }));
    });
  }, [orgCode]);

  useEffect(() => {
    setPreCalculatedData({
      availableProducts: calculateAvailableProducts.availableProducts,
      showProgramProduct: calculateAvailableProducts.showProgramProduct,
      programs: organizationPrograms,
      isLoading: loadingOrganizationPrograms,
    });
  }, [
    calculateAvailableProducts,
    organizationPrograms,
    loadingOrganizationPrograms,
  ]);

  return preCalculatedData;
};
